import { useEffect, useState, useRef } from 'react'
import { Login } from './screens/Login'
import { TotpChallenge } from './screens/TotpChallenge'
import { Home } from './screens/Home'
import { Presets } from './screens/Presets'
import { Users } from './screens/Users'
import { fetchMe, logout, ClientSocket } from './api'
import { useStore } from './store'

type Phase =
  | { kind: 'loading' }
  | { kind: 'login' }
  | { kind: 'totp'; continuationToken: string }
  | { kind: 'home' }

export default function App() {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' })
  const [tab, setTab] = useState<'router' | 'presets' | 'users'>('router')
  const sockRef = useRef<ClientSocket | null>(null)
  const pendingResolvers = useRef(new Map<string, (r: { ok: boolean; error?: string }) => void>())

  const { setUser, applySnapshot, applyState, setBridgeStatus } = useStore()
  const currentUser = useStore((s) => s.user)

  useEffect(() => {
    fetchMe().then((me) => {
      if (me) {
        setUser(me)
        connect()
        setPhase({ kind: 'home' })
      } else {
        setPhase({ kind: 'login' })
      }
    })
    return () => sockRef.current?.close()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function connect() {
    sockRef.current?.close()
    sockRef.current = new ClientSocket({
      onOpen: () => {},
      onClose: () => setBridgeStatus(false),
      onMessage: (msg) => {
        switch (msg.type) {
          case 'snapshot':
            applySnapshot(msg)
            break
          case 'state':
            applyState(msg)
            break
          case 'bridge.online':
            setBridgeStatus(true, Date.now())
            break
          case 'bridge.offline':
            setBridgeStatus(false, msg.lastSeen)
            break
          case 'take.result': {
            const resolver = pendingResolvers.current.get(msg.requestId)
            if (resolver) {
              resolver({ ok: msg.ok, error: msg.error })
              pendingResolvers.current.delete(msg.requestId)
            }
            break
          }
        }
      },
    })
    sockRef.current.connect()
  }

  async function onLoggedIn() {
    const me = await fetchMe()
    if (me) {
      setUser(me)
      connect()
      setPhase({ kind: 'home' })
    }
  }

  async function onLogout() {
    await logout()
    sockRef.current?.close()
    setUser(null)
    setPhase({ kind: 'login' })
  }

  function sendTake(destination: number, source: number): Promise<{ ok: boolean; error?: string }> {
    const requestId = crypto.randomUUID()
    return new Promise((resolve) => {
      pendingResolvers.current.set(requestId, resolve)
      sockRef.current?.sendTake({ requestId, destination, source, level: 0 })
      setTimeout(() => {
        if (pendingResolvers.current.has(requestId)) {
          pendingResolvers.current.delete(requestId)
          resolve({ ok: false, error: 'timeout' })
        }
      }, 12_000)
    })
  }

  if (phase.kind === 'loading') {
    return <div className="min-h-screen flex items-center justify-center text-sub">Loading…</div>
  }
  if (phase.kind === 'login') {
    return (
      <Login
        onLoggedIn={onLoggedIn}
        onTotpRequired={(continuationToken) => setPhase({ kind: 'totp', continuationToken })}
      />
    )
  }
  if (phase.kind === 'totp') {
    return (
      <TotpChallenge
        continuationToken={phase.continuationToken}
        onLoggedIn={onLoggedIn}
        onCancel={() => setPhase({ kind: 'login' })}
      />
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-bg text-ink">
      <div className="px-4 py-2 border-b border-border flex items-center gap-2 shrink-0">
        <div className="font-bold tracking-wider">ULTRIX</div>
        <div className="flex-1" />
        <button
          onClick={() => setTab('router')}
          className={`px-4 py-2 rounded-md border text-sm ${
            tab === 'router' ? 'bg-accent/20 border-accent' : 'bg-panel border-border hover:border-sub'
          }`}
        >
          Router
        </button>
        <button
          onClick={() => setTab('presets')}
          className={`px-4 py-2 rounded-md border text-sm ${
            tab === 'presets' ? 'bg-accent/20 border-accent' : 'bg-panel border-border hover:border-sub'
          }`}
        >
          Presets
        </button>
        {currentUser?.role === 'admin' && (
          <button
            onClick={() => setTab('users')}
            className={`px-4 py-2 rounded-md border text-sm ${
              tab === 'users' ? 'bg-accent/20 border-accent' : 'bg-panel border-border hover:border-sub'
            }`}
          >
            Users
          </button>
        )}
      </div>
      <div className="flex-1 min-h-0 flex flex-col">
        {tab === 'router' && <Home onSendTake={sendTake} onLogout={onLogout} />}
        {tab === 'presets' && <Presets />}
        {tab === 'users' && currentUser?.role === 'admin' && <Users />}
      </div>
    </div>
  )
}
